import { getContext, put, call, takeEvery } from 'redux-saga/effects';
import { FormAction as BaseFormAction, SubmissionError } from 'redux-form';
import { ApolloError, MutationOptions } from 'apollo-client';
import { SagaContext } from 'shared/types/common';
import { Services } from 'init/services';
import { getFormErrors } from 'shared/utils/formatError';
import * as dataLayer from 'shared/utils/gtmDataLayer';
import { ideaQuery } from 'queries/IdeaQuery/builder';
import * as mutation from 'mutations/CreateIdeaBuyOrder/mutation.gql';
import {
  CreateIdeaBuyOrderMutation,
  CreateIdeaBuyOrderMutationVariables,
} from 'mutations/CreateIdeaBuyOrder/__generated__/CreateIdeaBuyOrderMutation';

import { mainAccountQuery } from 'shared/queries/MainAccountsQuery/builder';
import { orderListQuery } from 'queries/OrderList';
import { actions as appActions } from 'actions';
import { routine } from './actions';
import { FormData } from './constraints';
import { Selector } from './selectors';
import { Props } from './index';

interface FormAction extends BaseFormAction {
  payload: {
    values: FormData;
    props: Props & Selector;
  };
}

// @translate
type FormErrors = 'Idea has procurement' | 'New unlimited orders are disabled';

export default function* saga() {
  const apolloClient: Services[SagaContext.ApolloClient] = yield getContext(
    SagaContext.ApolloClient
  );

  yield takeEvery(routine.TRIGGER, function* ({ payload: { values, props } }: FormAction) {
    const options: MutationOptions<
      CreateIdeaBuyOrderMutation,
      CreateIdeaBuyOrderMutationVariables
    > = {
      mutation,
      variables: {
        request: {
          amount: {
            value: +values.amount,
            currency: props.idea.enterAmount.currency,
          },
          ideaId: props.idea.id,
        },
      },
      refetchQueries: [
        ideaQuery({ id: props.idea.id }),
        mainAccountQuery(),
        orderListQuery({ archived: false }),
      ],
      awaitRefetchQueries: true,
    };

    try {
      yield call((opt: typeof options) => apolloClient.mutate(opt), options);

      dataLayer.push({
        eventCategory: 'form',
        eventAction: 'orderSubmit',
        eventLabel: `investments/${props.idea.id}`,
        eventValue: values.amount,
      });

      yield put(routine.success());
      yield put(appActions.showOrderSubmittedModal(props.idea.ideaType));
    } catch (e: ApolloError | any) {
      const errors = getFormErrors(e, (fieldName) =>
        fieldName === 'requestAmount' ? 'amount' : undefined
      );
      yield put(routine.failure(new SubmissionError<FormData, FormErrors>(errors)));
    }
  });
}
